import {
  addToDate,
  getEndOfDay,
  getFormInputDate,
  parseFormInputDate,
  useOrganization,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  EmptyState,
  Loader,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { useAppointments } from '../../../data-access/appointments';
import { usePriceDisplayMode } from '../../../data-access/price-display-mode';
import { useProposalHeaderImage } from '../../../data-access/proposalHeaderImage';
import { SectionWrapper } from '../../../patterns';
import EditPresentationSection, {
  EditPresentationFormInputs,
} from './sections/EditPresentationSection';

const FORM_ID = 'editPresentationForm';

const EditPresentationPage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const {
    currentProposal,
    isLoading: isLoadingProposal,
    updateAppointmentProposal,
  } = useAppointmentProposals();
  const { currentAppointment } = useAppointments();
  const { organization } = useOrganization();
  const {
    currentPriceDisplayMode,
    isLoading: isPriceDisplayModeLoading,
    isLoaded: isPriceDisplayModeLoaded,
    fetchPriceDisplayModes,
  } = usePriceDisplayMode();

  const {
    proposalHeaderImages,
    isLoading: isProposalHeaderImageLoading,
    isLoaded: isProposalHeaderImageLoaded,
    fetchProposalHeaderImages,
  } = useProposalHeaderImage();

  useEffect(() => {
    fetchPriceDisplayModes();
    organization && fetchProposalHeaderImages(organization.organizationId);
  }, [fetchPriceDisplayModes, fetchProposalHeaderImages, organization]);

  const defaultValues = useMemo(() => {
    const defaultProposalHeaderImage = proposalHeaderImages.find(
      (image) => image.isDefault
    );

    const defaultCoverPhoto = defaultProposalHeaderImage && {
      fileType: 'ORG_PROPOSAL_HEADER' as const,
      fileId: defaultProposalHeaderImage.proposalHeaderImageId,
    };

    return {
      headline:
        currentProposal?.presentationConfig?.headline?.trim() ||
        `${currentAppointment?.customer.name}'s Proposal`,
      subheadline:
        currentProposal?.presentationConfig?.subheadline?.trim() ||
        `Designed for your home at ${currentAppointment?.location.address1}`,
      summary: currentProposal?.presentationConfig?.summary || '',
      theme:
        currentProposal?.presentationConfig?.theme ||
        organization?.proposalConfiguration?.defaultTheme ||
        'AIRSHIP_BLUE',
      coverPhoto: currentProposal?.presentationConfig?.coverPhoto ||
        defaultCoverPhoto || {
          fileType: 'NO_IMAGE',
        },
      priceDisplayMode:
        currentProposal?.presentationConfig?.priceDisplayMode ||
        currentPriceDisplayMode?.priceDisplayMode ||
        'FINANCING_PAYMENT',
      showAIOptionsSummary:
        currentProposal?.presentationConfig?.showAIOptionsSummary ||
        currentProposal?.presentationConfig?.showAIOptionsSummary === undefined,
      expiryDate: getFormInputDate(
        currentProposal?.expiryDate
          ? new Date(currentProposal.expiryDate)
          : addToDate(
              organization?.proposalConfiguration?.expirationDays || 365,
              'd'
            )
      ),
      optionItemsDisplayOrder:
        currentProposal?.presentationConfig.optionItemsDisplayOrder ||
        organization?.proposalConfiguration.optionItemsDisplayOrder ||
        [],
    };
  }, [
    currentAppointment?.customer.name,
    currentAppointment?.location.address1,
    currentPriceDisplayMode?.priceDisplayMode,
    currentProposal?.presentationConfig.coverPhoto,
    currentProposal?.presentationConfig.headline,
    currentProposal?.presentationConfig.optionItemsDisplayOrder,
    currentProposal?.presentationConfig.priceDisplayMode,
    currentProposal?.presentationConfig.showAIOptionsSummary,
    currentProposal?.presentationConfig.subheadline,
    currentProposal?.presentationConfig.summary,
    currentProposal?.presentationConfig.theme,
    currentProposal?.expiryDate,
    organization?.proposalConfiguration.defaultTheme,
    organization?.proposalConfiguration.optionItemsDisplayOrder,
    organization?.proposalConfiguration.expirationDays,
    proposalHeaderImages,
  ]);

  if (
    (isPriceDisplayModeLoading || isProposalHeaderImageLoading) &&
    !(isPriceDisplayModeLoaded && isProposalHeaderImageLoaded)
  ) {
    return <Loader isLoading message="Loading presentation settings" />;
  }

  if (!currentProposal || !organization) {
    return (
      <EmptyState
        title="Appointment not found"
        subtitle="Oops! The requested appointment was not found."
      />
    );
  }

  const handleSave = (data: EditPresentationFormInputs) => {
    const { expiryDate: expiryDateInput, ...presentationData } = data;
    const expiryDate = getEndOfDay(
      parseFormInputDate(expiryDateInput)
    ).toISOString();

    updateAppointmentProposal(
      currentProposal.appointmentId,
      currentProposal.appointmentProposalId,
      {
        expiryDate,
        presentationConfig: {
          ...currentProposal.presentationConfig,
          ...presentationData,
          coverPhoto: data.coverPhoto || undefined,
        },
      }
    )
      .then(() => navigate(-1))
      .catch(() => {
        showToast('Failed to update proposal presentation', 'error');
      });
  };

  return (
    <SectionWrapper
      maxWidth={'50rem'}
      width={'100%'}
      header="Edit Proposal Presentation"
      subheader="Edit the proposal presentation to customize the look and feel of the proposal."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../',
          label: 'Cancel',
        },
        right: {
          type: 'submit',
          component: LoadingButton,
          form: FORM_ID,
          label: 'Save',
          loading: isLoadingProposal,
          disabled: isLoadingProposal,
        },
      }}
    >
      <EditPresentationSection
        formId={FORM_ID}
        defaultValues={defaultValues}
        onSubmit={handleSave}
        appointmentId={currentProposal.appointmentId}
        organizationId={organization.organizationId}
      />
    </SectionWrapper>
  );
};

export default EditPresentationPage;
